export const DOCUMENT_SOURCE_TYPES = ['upload', 'external'];
export const DOCUMENT_CATEGORIES = ['join_requirement', 'reference'];
export const DOCUMENT_VISIBILITIES = ['public', 'members', 'confirmed_bookers'];

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function sanitizeFileName(value = '') {
  return normalizeString(value)
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

export function getDocumentSourceUrl(doc = {}) {
  return doc?.sourceType === 'upload'
    ? normalizeString(doc?.fileUrl)
    : normalizeString(doc?.externalUrl);
}

export function getDocumentDownloadFileName(doc = {}) {
  const explicitName = sanitizeFileName(doc?.fileName);
  if (explicitName) return explicitName;

  const titleName = sanitizeFileName(doc?.title);
  return titleName || 'document';
}

export function getDocumentTypeLabel(doc = {}) {
  const sourceUrl = getDocumentSourceUrl(doc);
  const fileName = normalizeString(doc?.fileName) || normalizeString(doc?.title) || sourceUrl;
  const extensionMatch = fileName.toLowerCase().match(/\.([a-z0-9]+)(?:[?#].*)?$/);
  const ext = extensionMatch?.[1] || '';

  if (ext === 'pdf') return 'PDF';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
  if (['doc', 'docx'].includes(ext)) return 'Word document';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'spreadsheet';
  if (['ppt', 'pptx'].includes(ext)) return 'presentation';
  if (['txt', 'md', 'rtf'].includes(ext)) return 'document';
  return doc?.sourceType === 'external' ? 'link' : 'file';
}

export function getDocumentActionLabels(doc = {}) {
  const typeLabel = getDocumentTypeLabel(doc);
  const isLink = typeLabel === 'link';
  return {
    view: isLink ? 'Open link' : `View ${typeLabel}`,
    download: isLink ? 'Download' : `Download ${typeLabel}`,
  };
}

export async function triggerDocumentDownload(doc = {}) {
  const sourceUrl = getDocumentSourceUrl(doc);
  if (!sourceUrl || typeof document === 'undefined') return;

  const fileName = getDocumentDownloadFileName(doc);

  try {
    const response = await fetch(sourceUrl);
    if (!response.ok) throw new Error('Document download failed.');
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
    return;
  } catch {
    const link = document.createElement('a');
    link.href = sourceUrl;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}

export function normalizeTeamDocument(doc = {}, fallbackId = '') {
  const sourceType = DOCUMENT_SOURCE_TYPES.includes(doc?.sourceType) ? doc.sourceType : 'external';
  const category = DOCUMENT_CATEGORIES.includes(doc?.category) ? doc.category : 'reference';
  const visibility = DOCUMENT_VISIBILITIES.includes(doc?.visibility) ? doc.visibility : 'public';

  return {
    id: normalizeString(doc?.id) || fallbackId,
    title: normalizeString(doc?.title),
    description: normalizeString(doc?.description),
    sourceType,
    fileUrl: normalizeString(doc?.fileUrl),
    externalUrl: normalizeString(doc?.externalUrl),
    fileName: normalizeString(doc?.fileName),
    storagePath: normalizeString(doc?.storagePath),
    category,
    visibility,
    linkedEventIds: Array.from(new Set((Array.isArray(doc?.linkedEventIds) ? doc.linkedEventIds : []).map(normalizeString).filter(Boolean))),
    linkedCourseIds: Array.from(new Set((Array.isArray(doc?.linkedCourseIds) ? doc.linkedCourseIds : []).map(normalizeString).filter(Boolean))),
    eventGroupLabel: normalizeString(doc?.eventGroupLabel),
    acceptanceLabel: normalizeString(doc?.acceptanceLabel),
    versionLabel: normalizeString(doc?.versionLabel),
    active: doc?.active !== false,
    sortOrder: normalizeNumber(doc?.sortOrder, 0),
    updatedAt: doc?.updatedAt || null,
  };
}

export function sortTeamDocuments(a = {}, b = {}) {
  const sortDiff = normalizeNumber(a?.sortOrder, 0) - normalizeNumber(b?.sortOrder, 0);
  if (sortDiff !== 0) return sortDiff;

  const titleA = normalizeString(a?.title).toLowerCase();
  const titleB = normalizeString(b?.title).toLowerCase();
  if (titleA !== titleB) return titleA.localeCompare(titleB);

  return normalizeString(a?.id).localeCompare(normalizeString(b?.id));
}

export function normalizeTeamDocuments(docs = []) {
  return (Array.isArray(docs) ? docs : [])
    .map((doc, index) => normalizeTeamDocument(doc, `document-${index + 1}`))
    .filter(doc => Boolean(doc.id))
    .sort(sortTeamDocuments);
}

export function isJoinRequirementDocument(doc = {}) {
  return normalizeTeamDocument(doc).category === 'join_requirement';
}

export function isActiveDocument(doc = {}) {
  const normalized = normalizeTeamDocument(doc);
  return normalized.active && Boolean(getDocumentSourceUrl(normalized));
}

export function canViewerSeeDocument(doc = {}, { isAdmin = false, isMember = false, isConfirmedBooker = false } = {}) {
  const normalized = normalizeTeamDocument(doc);
  if (isAdmin) return true;
  if (!normalized.active) return false;
  if (normalized.visibility === 'public') return true;
  if (normalized.visibility === 'confirmed_bookers') return isConfirmedBooker;
  return isMember;
}

export function getVisibleDocuments(docs = [], viewer = {}) {
  return normalizeTeamDocuments(docs).filter(doc => canViewerSeeDocument(doc, viewer));
}

export function getRequiredJoinDocuments(docs = []) {
  return normalizeTeamDocuments(docs).filter(doc => isActiveDocument(doc) && isJoinRequirementDocument(doc));
}
